import { create } from 'zustand';
import _ from 'lodash';
import { INITIAL_STATE } from '../constants/initialValue';
import {
	HudData,
	ModeData,
	MapData,
	LocationData,
	DisplayData,
	GoalData,
	SystemData,
	SoundData,
	LoggerData,
	WelcomeData,
	RemoteData,
	VersionData,
	FrameStreams,
	MissionData,
	AutonomousKartData,
} from '../types/store';

// Store 타입 정의
type StoreState<T> = {
	data: T;
	subscribers: Set<(state: StoreState<T>) => void>;
	update: (newData: T) => void;
	setState: (newData: T) => void;
	subscribe: (callback: (state: StoreState<T>) => void) => () => void;
};

// Simple stores - direct mutation with manual subscriber notification
// This avoids React re-renders while still notifying non-React subscribers
const createDataStore = <T>(initialData: T) => {
	return create<StoreState<T>>((set, get) => ({
		data: initialData,
		subscribers: new Set(),
		// Ref 방식: mutation으로 업데이트 (React 리렌더링 없음)
		update: (newData: T) => {
			const state = get();
			if (!_.isEqual(state.data, newData)) {
				state.data = newData;
				state.subscribers.forEach((callback) => callback(state));
			}
		},
		// State 방식: set()으로 업데이트 (React 리렌더링 트리거)
		setState: (newData: T | ((prev: T) => T)) => {
			const state = get();
			const nextData =
				typeof newData === 'function'
					? (newData as (prev: T) => T)(state.data)
					: newData;

			if (!_.isEqual(state.data, nextData)) {
				set({ data: nextData } as Partial<StoreState<T>>);
				state.subscribers.forEach((callback) => callback(get()));
			}
		},
		subscribe: (callback: (state: StoreState<T>) => void) => {
			const state = get();
			state.subscribers.add(callback);
			return () => state.subscribers.delete(callback);
		},
	}));
};

export const hudStore = createDataStore(INITIAL_STATE.hud as HudData);
export const modeStore = createDataStore(INITIAL_STATE.mode as ModeData);
export const mapStore = createDataStore(INITIAL_STATE.map as MapData);
export const locationStore = createDataStore(
	INITIAL_STATE.location as LocationData,
);
export const displayStore = createDataStore(
	INITIAL_STATE.display as DisplayData,
);
export const goalStore = createDataStore(INITIAL_STATE.goal as GoalData);
export const systemStore = createDataStore(INITIAL_STATE.system as SystemData);
export const soundStore = createDataStore(INITIAL_STATE.sound as SoundData);
export const loggerStore = createDataStore(INITIAL_STATE.logger as LoggerData);
export const welcomeStore = createDataStore(
	INITIAL_STATE.welcome as WelcomeData,
);
export const remoteStore = createDataStore(INITIAL_STATE.remote as RemoteData);
export const versionStore = createDataStore(
	INITIAL_STATE.version as VersionData,
);
export const missionStore = createDataStore(
	INITIAL_STATE.mission as MissionData,
);
export const autonomousKartStore = createDataStore(
	INITIAL_STATE.autonomousKart as AutonomousKartData,
);

// Frame store - special store for dynamic frame streams data
// Merges new keys and updates existing ones
type FrameStoreState = {
	data: FrameStreams;
	subscribers: Set<(state: FrameStoreState) => void>;
	update: (newStreams: FrameStreams) => void;
	subscribe: (callback: (state: FrameStoreState) => void) => () => void;
};

const createFrameStore = () => {
	return create<FrameStoreState>((set, get) => ({
		data: {},
		subscribers: new Set(),
		update: (newStreams: FrameStreams) => {
			if (!newStreams) return;
			const state = get();
			// Merge new streams with existing data
			const mergedData = { ...state.data, ...newStreams };
			if (!_.isEqual(state.data, mergedData)) {
				state.data = mergedData;
				state.subscribers.forEach((callback) => callback(state));
			}
		},
		subscribe: (callback: (state: FrameStoreState) => void) => {
			const state = get();
			state.subscribers.add(callback);
			return () => state.subscribers.delete(callback);
		},
	}));
};

export const frameStore = createFrameStore();

// React hooks - for components that need reactive updates
export const useHudStore = <R = HudData>(
	selector?: (state: StoreState<HudData>) => R,
) =>
	hudStore(
		selector || (((state) => state.data) as (state: StoreState<HudData>) => R),
	);
export const useModeStore = <R = ModeData>(
	selector?: (state: StoreState<ModeData>) => R,
) =>
	modeStore(
		selector || (((state) => state.data) as (state: StoreState<ModeData>) => R),
	);
export const useMapStore = <R = MapData>(
	selector?: (state: StoreState<MapData>) => R,
) =>
	mapStore(
		selector || (((state) => state.data) as (state: StoreState<MapData>) => R),
	);
export const useLocationStore = <R = LocationData>(
	selector?: (state: StoreState<LocationData>) => R,
) =>
	locationStore(
		selector ||
			(((state) => state.data) as (state: StoreState<LocationData>) => R),
	);
export const useDisplayStore = <R = DisplayData>(
	selector?: (state: StoreState<DisplayData>) => R,
) =>
	displayStore(
		selector ||
			(((state) => state.data) as (state: StoreState<DisplayData>) => R),
	);
export const useGoalStore = <R = GoalData>(
	selector?: (state: StoreState<GoalData>) => R,
) =>
	goalStore(
		selector || (((state) => state.data) as (state: StoreState<GoalData>) => R),
	);
export const useSystemStore = <R = SystemData>(
	selector?: (state: StoreState<SystemData>) => R,
) =>
	systemStore(
		selector ||
			(((state) => state.data) as (state: StoreState<SystemData>) => R),
	);
export const useSoundStore = <R = SoundData>(
	selector?: (state: StoreState<SoundData>) => R,
) =>
	soundStore(
		selector ||
			(((state) => state.data) as (state: StoreState<SoundData>) => R),
	);
export const useLoggerStore = <R = LoggerData>(
	selector?: (state: StoreState<LoggerData>) => R,
) =>
	loggerStore(
		selector ||
			(((state) => state.data) as (state: StoreState<LoggerData>) => R),
	);
export const useWelcomeStore = <R = WelcomeData>(
	selector?: (state: StoreState<WelcomeData>) => R,
) =>
	welcomeStore(
		selector ||
			(((state) => state.data) as (state: StoreState<WelcomeData>) => R),
	);
export const useRemoteStore = <R = RemoteData>(
	selector?: (state: StoreState<RemoteData>) => R,
) =>
	remoteStore(
		selector ||
			(((state) => state.data) as (state: StoreState<RemoteData>) => R),
	);
export const useVersionStore = <R = VersionData>(
	selector?: (state: StoreState<VersionData>) => R,
) =>
	versionStore(
		selector ||
			(((state) => state.data) as (state: StoreState<VersionData>) => R),
	);
export const useMissionStore = <R = MissionData>(
	selector?: (state: StoreState<MissionData>) => R,
) =>
	missionStore(
		selector ||
			(((state) => state.data) as (state: StoreState<MissionData>) => R),
	);

export const useAutonomousKartStore = <R = AutonomousKartData>(
	selector?: (state: StoreState<AutonomousKartData>) => R,
) =>
	autonomousKartStore(
		selector ||
			(((state) => state.data) as (state: StoreState<AutonomousKartData>) => R),
	);
